import { useMutation } from '@apollo/client';
import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertActionUpdateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertActionUpdateDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useInsertActionUpdateTRPC } from './useInsertActionUpdateTRPC';

type InsertActionUpdateInput = {
  ParentActionId: string;
  Title: string;
  Description: string;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useInsertActionUpdate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertActionUpdateGraphQL, graphqlState] = useMutation(
    InsertActionUpdateDocument,
    {}
  );

  // tRPC mutation
  const trpcMutation = useInsertActionUpdateTRPC();

  const insertActionUpdate = async (
    variables: InsertActionUpdateInput
  ): Promise<InsertActionUpdateMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertActionUpdate(variables);
    }

    const result = await insertActionUpdateGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert action update');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertActionUpdate,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertActionUpdate,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
